// One-shot localStorage rename when the project was renamed Agora → Verdant.
// Runs once at app boot from main.jsx, before any other code reads the keys.
// Idempotent: if a verdant_* key already exists, the corresponding agora_*
// is left alone (and the migration is considered done).

const RENAMES = [
  ['agora_pubkey',         'verdant_pubkey'],
  ['agora_login_type',     'verdant_login_type'],
  ['agora_nsec_hex',       'verdant_nsec_hex'],
  ['agora_ncryptsec',      'verdant_ncryptsec'],
  ['agora_jurisdiction',   'verdant_jurisdiction'],
  ['agora_votes',          'verdant_votes'],
  ['agora_my_events',      'verdant_my_events'],
]

const SNAPSHOT_OLD_PREFIX = 'agora_snapshots_'
const SNAPSHOT_NEW_PREFIX = 'verdant_snapshots_'

// UX24: the old un-namespaced 'verdant_votes' key carried whatever
// vote map the last signed-in account left behind. Reading it as the
// fresh-mount initializer leaked one account's voted-state into the
// next account's UI on shared devices. We've moved away from
// localStorage caching for votes entirely (userVotes is derived from
// voteIndex); this is a one-shot cleanup so the orphan key stops
// sitting in storage. Safe to run repeatedly.
const LEGACY_VOTES_KEY = 'verdant_votes'
// O06: the analytics first-vote dedupe used to be a single un-
// namespaced key that stored the most-recent voting pubkey as its
// value. After switching to per-pubkey keys, the legacy entry is
// orphan; clear it for tidiness and to remove the leak surface.
const LEGACY_FIRST_VOTE_KEY = 'verdant_first_vote_fired'

export function migrateLocalStorageKeys() {
  if (typeof localStorage === 'undefined') return
  try {
    for (const [oldKey, newKey] of RENAMES) {
      const newVal = localStorage.getItem(newKey)
      if (newVal !== null) continue                  // already migrated
      const oldVal = localStorage.getItem(oldKey)
      if (oldVal === null) continue                  // never had it
      localStorage.setItem(newKey, oldVal)
      localStorage.removeItem(oldKey)
    }

    // Snapshot keys are dynamic (verdant_snapshots_<pollId>). Walk the
    // store once and rename any that still use the old prefix.
    const toRename = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(SNAPSHOT_OLD_PREFIX)) toRename.push(k)
    }
    for (const oldKey of toRename) {
      const newKey = SNAPSHOT_NEW_PREFIX + oldKey.slice(SNAPSHOT_OLD_PREFIX.length)
      if (localStorage.getItem(newKey) !== null) {
        localStorage.removeItem(oldKey)              // already migrated
        continue
      }
      const val = localStorage.getItem(oldKey)
      if (val !== null) {
        localStorage.setItem(newKey, val)
        localStorage.removeItem(oldKey)
      }
    }

    // UX24: clear the orphan un-namespaced votes key. Whatever votes
    // are there belong to a prior login and shouldn't be visible to
    // anyone else who signs in on this device.
    if (localStorage.getItem(LEGACY_VOTES_KEY) !== null) {
      localStorage.removeItem(LEGACY_VOTES_KEY)
    }
    // O06: clear the legacy first-vote dedupe key — replaced by
    // per-pubkey keys in analytics.js.
    if (localStorage.getItem(LEGACY_FIRST_VOTE_KEY) !== null) {
      localStorage.removeItem(LEGACY_FIRST_VOTE_KEY)
    }
  } catch (e) {
    console.warn('[Verdant] localStorage migration failed', e)
  }
}
